import { useState, useCallback } from 'react';
import api from '../services/api';

interface CreateShipmentModalProps {
  onClose: () => void;
  onCreated: () => void;
}

interface ShipmentItem {
  description: string;
  qty: number;
  weight: number;
  length: number;
  width: number;
  height: number;
  freightClass: string;
  packagingType: string;
}

interface Destination {
  company: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  contactName: string;
  phone: string;
  shipType: string;
  liftgate: boolean;
  insideDelivery: boolean;
}

const FREIGHT_CLASSES = [
  '50', '55', '60', '65', '70', '77.5', '85', '92.5',
  '100', '110', '125', '150', '175', '200', '250', '300', '400', '500',
];

const PACKAGING_TYPES = ['pallet', 'crate', 'drum', 'bundle'];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS','MP',
];

function getNextBusinessDay(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  // Skip weekends
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }
  return date.toISOString().split('T')[0];
}

function createDefaultItem(): ShipmentItem {
  return {
    description: 'V-Track Motorized Screen Systems (NMFC 162890)',
    qty: 1,
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    freightClass: '85',
    packagingType: 'pallet',
  };
}

export default function CreateShipmentModal({ onClose, onCreated }: CreateShipmentModalProps) {
  const [destination, setDestination] = useState<Destination>({
    company: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'us',
    contactName: '',
    phone: '',
    shipType: 'business_dock',
    liftgate: false,
    insideDelivery: false,
  });

  const [items, setItems] = useState<ShipmentItem[]>([createDefaultItem()]);
  const [pickupDate, setPickupDate] = useState(getNextBusinessDay());
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [error, setError] = useState('');
  const [quotes, setQuotes] = useState<any[] | null>(null);

  const updateDestination = (field: keyof Destination, value: string | boolean) => {
    setDestination(prev => ({ ...prev, [field]: value }));
  };

  const loadFromOrder = useCallback(async () => {
    if (!orderNumber.trim()) {
      setError('Enter an order number to load');
      return;
    }
    setLoadingOrder(true);
    setError('');
    try {
      // Look up order by order number
      const order = await api.lookupOrder(orderNumber.trim());
      setOrderId(order.id);

      // Load destination from ship_to_address
      const addr = typeof order.ship_to_address === 'string'
        ? JSON.parse(order.ship_to_address)
        : (order.ship_to_address || {});

      if (addr) {
        // Map full state name to abbreviation if needed
        const stateAbbr = addr.state?.length > 2
          ? US_STATES.find(s => addr.state?.toLowerCase().startsWith(s.toLowerCase())) || addr.state
          : (addr.state || '');

        setDestination(prev => ({
          ...prev,
          company: addr.company || order.dealer_name || prev.company,
          address: addr.street || addr.address || prev.address,
          city: addr.city || prev.city,
          state: stateAbbr || prev.state,
          zip: addr.zip || prev.zip,
          contactName: addr.contactName || addr.contact || `${order.first_name || ''} ${order.last_name || ''}`.trim() || prev.contactName,
          phone: addr.phone || order.dealer_phone || prev.phone,
          shipType: addr.residential ? 'residential' : prev.shipType,
          liftgate: addr.liftgate || prev.liftgate,
        }));
      }

      // Auto-calculate shipping dims from order items (1 box per screen)
      try {
        const dims = await api.calcShippingDims(order.id);
        if (dims.boxes && dims.boxes.length > 0) {
          // Create one shipment item per box (one per screen unit)
          setItems(dims.boxes.map((box: any) => ({
            description: `Screen Unit #${box.itemNumber} (NMFC 162890)`,
            qty: 1,
            weight: box.weight,
            length: box.length,
            width: box.width,
            height: box.height,
            freightClass: String(dims.freightClass || 85),
            packagingType: 'crate',
          })));
        }
      } catch {
        // Dims calc failed — leave items as default
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order');
    } finally {
      setLoadingOrder(false);
    }
  }, [orderNumber]);

  const updateItem = (index: number, field: keyof ShipmentItem, value: string | number) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addRow = () => {
    setItems(prev => [...prev, createDefaultItem()]);
  };

  const removeRow = (index: number) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!destination.company.trim()) {
      setError('Company name is required');
      return;
    }
    if (!destination.address.trim()) {
      setError('Address is required');
      return;
    }
    if (!destination.city.trim()) {
      setError('City is required');
      return;
    }
    if (!destination.state.trim()) {
      setError('State is required');
      return;
    }
    if (!destination.zip.trim()) {
      setError('ZIP code is required');
      return;
    }
    if (!pickupDate) {
      setError('Pickup date is required');
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.description.trim()) {
        setError(`Item ${i + 1}: Description is required`);
        return;
      }
      if (item.qty < 1) {
        setError(`Item ${i + 1}: Quantity must be at least 1`);
        return;
      }
      if (item.weight <= 0) {
        setError(`Item ${i + 1}: Weight must be greater than 0`);
        return;
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const payload: any = {
        destination: {
          company: destination.company.trim(),
          address: destination.address.trim(),
          city: destination.city.trim(),
          state: destination.state.trim(),
          zip: destination.zip.trim(),
          country: destination.country,
          contactName: destination.contactName.trim(),
          phone: destination.phone.trim(),
          shipType: destination.shipType,
          liftgate: destination.liftgate,
          insideDelivery: destination.insideDelivery,
        },
        items: items.map(item => ({
          description: item.description.trim(),
          qty: item.qty,
          weight: item.weight,
          length: item.length,
          width: item.width,
          height: item.height,
          freightClass: item.freightClass,
          packagingType: item.packagingType,
        })),
        pickupDate,
      };

      if (orderId.trim()) {
        payload.orderId = orderId.trim();
      }

      const result = await api.createFVShipment(payload);

      // Show top 3 quotes if available
      const returnedQuotes = result.quotes || result.rates || [];
      if (returnedQuotes.length > 0) {
        setQuotes(returnedQuotes.slice(0, 3));
        // Auto-close after brief display
        setTimeout(() => {
          onCreated();
        }, 3000);
      } else {
        onCreated();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shipment');
    } finally {
      setSubmitting(false);
    }
  };

  // Show quotes result briefly
  if (quotes) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-white mb-4">Shipment Created</h2>
          <p className="text-gray-300 text-sm mb-4">Top quotes received:</p>
          <div className="space-y-2 mb-6">
            {quotes.map((q: any, i: number) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                <span className="text-white text-sm font-medium">
                  {q.carrierName || q.carrier_name || q.carrier || 'Carrier'}
                </span>
                <span className="text-green-400 font-bold">
                  ${Number(q.amount || q.totalAmount || q.total_amount || q.rate || 0).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-xs text-center">Closing automatically...</p>
        </div>
      </div>
    );
  }

  const inputClass = 'w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent';
  const labelClass = 'block text-sm text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 rounded-t-xl z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">Create FreightView Shipment</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {error && (
            <div className="bg-red-900/40 border border-red-700 rounded-lg px-4 py-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* ======================== DESTINATION ======================== */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3">Destination</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className={labelClass}>Company *</label>
                <input
                  type="text"
                  value={destination.company}
                  onChange={e => updateDestination('company', e.target.value)}
                  className={inputClass}
                  placeholder="Company name"
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Address *</label>
                <input
                  type="text"
                  value={destination.address}
                  onChange={e => updateDestination('address', e.target.value)}
                  className={inputClass}
                  placeholder="Street address"
                />
              </div>
              <div>
                <label className={labelClass}>City *</label>
                <input
                  type="text"
                  value={destination.city}
                  onChange={e => updateDestination('city', e.target.value)}
                  className={inputClass}
                  placeholder="City"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>State *</label>
                  <select
                    value={destination.state}
                    onChange={e => updateDestination('state', e.target.value)}
                    className={inputClass}
                  >
                    <option value="">--</option>
                    {US_STATES.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>ZIP *</label>
                  <input
                    type="text"
                    value={destination.zip}
                    onChange={e => updateDestination('zip', e.target.value)}
                    className={inputClass}
                    placeholder="ZIP code"
                    maxLength={10}
                  />
                </div>
              </div>
              <div>
                <label className={labelClass}>Country</label>
                <input
                  type="text"
                  value={destination.country}
                  onChange={e => updateDestination('country', e.target.value)}
                  className={inputClass}
                  placeholder="us"
                  maxLength={2}
                />
              </div>
              <div>
                <label className={labelClass}>Contact Name</label>
                <input
                  type="text"
                  value={destination.contactName}
                  onChange={e => updateDestination('contactName', e.target.value)}
                  className={inputClass}
                  placeholder="Contact name"
                />
              </div>
              <div>
                <label className={labelClass}>Phone</label>
                <input
                  type="text"
                  value={destination.phone}
                  onChange={e => updateDestination('phone', e.target.value)}
                  className={inputClass}
                  placeholder="(555) 555-5555"
                />
              </div>
              <div>
                <label className={labelClass}>Ship Type</label>
                <select
                  value={destination.shipType}
                  onChange={e => updateDestination('shipType', e.target.value)}
                  className={inputClass}
                >
                  <option value="business_dock">Business with Dock</option>
                  <option value="residential">Residential</option>
                  <option value="business_no_dock">Business without Dock</option>
                </select>
              </div>
            </div>

            {/* Accessorials */}
            <div className="flex gap-6 mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={destination.liftgate}
                  onChange={e => updateDestination('liftgate', e.target.checked)}
                  className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-indigo-500"
                />
                Liftgate Required
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={destination.insideDelivery}
                  onChange={e => updateDestination('insideDelivery', e.target.checked)}
                  className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-indigo-500 focus:ring-indigo-500"
                />
                Inside Delivery
              </label>
            </div>
          </section>

          {/* ======================== ITEMS ======================== */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">Items</h3>
              <button
                type="button"
                onClick={addRow}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                + Add Row
              </button>
            </div>

            <div className="space-y-3">
              {items.map((item, idx) => (
                <div key={idx} className="bg-gray-750 border border-gray-600 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-300">Item {idx + 1}</span>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <div className="md:col-span-2">
                      <label className={labelClass}>Description</label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateItem(idx, 'description', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    <div>
                      <label className={labelClass}>Qty</label>
                      <input
                        type="number"
                        min={1}
                        value={item.qty}
                        onChange={e => updateItem(idx, 'qty', parseInt(e.target.value) || 1)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Weight (lbs)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.weight || ''}
                        onChange={e => updateItem(idx, 'weight', parseFloat(e.target.value) || 0)}
                        className={inputClass}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Length (in)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.length || ''}
                        onChange={e => updateItem(idx, 'length', parseFloat(e.target.value) || 0)}
                        className={inputClass}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Width (in)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.width || ''}
                        onChange={e => updateItem(idx, 'width', parseFloat(e.target.value) || 0)}
                        className={inputClass}
                        placeholder="0"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    <div>
                      <label className={labelClass}>Height (in)</label>
                      <input
                        type="number"
                        min={0}
                        value={item.height || ''}
                        onChange={e => updateItem(idx, 'height', parseFloat(e.target.value) || 0)}
                        className={inputClass}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Freight Class</label>
                      <select
                        value={item.freightClass}
                        onChange={e => updateItem(idx, 'freightClass', e.target.value)}
                        className={inputClass}
                      >
                        {FREIGHT_CLASSES.map(fc => (
                          <option key={fc} value={fc}>{fc}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Packaging Type</label>
                      <select
                        value={item.packagingType}
                        onChange={e => updateItem(idx, 'packagingType', e.target.value)}
                        className={inputClass}
                      >
                        {PACKAGING_TYPES.map(pt => (
                          <option key={pt} value={pt}>
                            {pt.charAt(0).toUpperCase() + pt.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ======================== PICKUP DATE ======================== */}
          <section>
            <h3 className="text-lg font-semibold text-white mb-3">Pickup Date</h3>
            <div className="max-w-xs">
              <input
                type="date"
                value={pickupDate}
                onChange={e => setPickupDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </section>

          {/* ======================== LOAD FROM ORDER ======================== */}
          <section className="bg-indigo-900/20 border border-indigo-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-3">Load from Order</h3>
            <p className="text-sm text-gray-400 mb-3">
              Enter an order number to auto-fill destination and calculate shipping dims/weight.
            </p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className={labelClass}>Order Number</label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={e => setOrderNumber(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadFromOrder()}
                  className={inputClass}
                  placeholder="e.g. VT26020007"
                />
              </div>
              <button
                onClick={loadFromOrder}
                disabled={loadingOrder}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
              >
                {loadingOrder ? 'Loading...' : 'Load Order'}
              </button>
            </div>
            {orderId && (
              <p className="text-xs text-green-400 mt-2">Linked to order ID: {orderId.substring(0, 8)}...</p>
            )}
          </section>

          {/* ======================== ACTIONS ======================== */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors font-medium"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Creating...
                </span>
              ) : (
                'Create Shipment'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
